import {
	createContext,
	useContext,
	useEffect,
	useState,
	ReactNode,
	useMemo,
} from 'react';
import { breakpoints } from '@quizzach/styles';

interface WindowListener {
	width: number;
	height: number;
	isMobile: boolean;
	touchDevice: boolean;
}

const getDefaultDimensions = (): WindowListener => {
	const width = typeof window !== 'undefined' ? window.innerWidth : 0;
	const height = typeof window !== 'undefined' ? window.innerHeight : 0;
	return {
		width,
		height,
		isMobile: width < 1000,
		touchDevice: width < breakpoints.smallDesktop,
	};
};

const WindowListenerContext = createContext<WindowListener>(
	getDefaultDimensions()
);

export const WindowListenerProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const [dimensions, setDimensions] = useState<{
		width: number;
		height: number;
	}>({
		width: typeof window !== 'undefined' ? window.innerWidth : 0,
		height: typeof window !== 'undefined' ? window.innerHeight : 0,
	});

	useEffect(() => {
		if (typeof window === 'undefined') return;

		let timeoutId: ReturnType<typeof setTimeout> | undefined;

		const handleResize = () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			timeoutId = setTimeout(() => {
				setDimensions({ width: window.innerWidth, height: window.innerHeight });
			}, 100); // 100ms debounce
		};

		window.addEventListener('resize', handleResize);
		return () => {
			window.removeEventListener('resize', handleResize);
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, []);

	// Memoize the context value with derived properties
	const value = useMemo<WindowListener>(
		() => ({
			width: dimensions.width,
			height: dimensions.height,
			isMobile: dimensions.width < 1000,
			touchDevice: dimensions.width < breakpoints.smallDesktop,
		}),
		[dimensions.width, dimensions.height]
	);

	return (
		<WindowListenerContext.Provider value={value}>
			{children}
		</WindowListenerContext.Provider>
	);
};

export const useWindowListener = () => useContext(WindowListenerContext);
